import type { GuidePredicate } from "./predicate.js";
import { AI_NOT_READY_REASONS, type AiNotReadyReason } from "./ready.js";
import type { GuideNumericSignalKey, GuideSignalKey } from "./signals.js";

/**
 * 🔴🔴 US-AIV2-tourguide-0004 (@UC0360 · TG-INV-1 · QĐ PO Q3) — CÂU THẺ = hàm thuần của (tín hiệu ĐÃ BIẾT, kết quả `ready()` của nút).
 *
 * Trước task này mỗi thẻ có MỘT câu, viết cho "đường suôn": nút hiện, nhóm có STK, lời báo chờ người giữ quỹ đối chiếu. Máy đo `0001`
 * đo được câu đó sai đúng ở chỗ tiền đi: nhóm chưa có STK (Q1) · người mà lời báo VÀO SỔ NGAY (K15) · quỹ chỉ người giữ quỹ ghi (K10 —
 * câu vẫn mời "báo lại" khi nút đã bị bỏ) · đợt không ai nhận được lời nhắc (K11).
 *
 * Mô hình: thẻ giữ `templateVi` = câu "có nút" mặc định (biến thể ẩn `BASE`, chỉ cho kết quả `ready`), và khai thêm `variants`:
 *  - `when` — điều kiện AND trên tín hiệu (như `GuideCard.when`). Tín hiệu KHÔNG BIẾT ⇒ biến thể không khớp (không đoán) — câu rơi về
 *    biến thể chung hơn, KHÔNG làm câm cả thẻ (khác `GuideCard.when`, nơi tín hiệu không biết loại cả thẻ).
 *  - `outcomes` — chỉ cho thẻ có nút: biến thể dùng cho kết quả `ready()` nào (`ready` · `unknown` · mỗi `AiNotReadyReason`). Thẻ không nút
 *    bỏ trống (biến thể chỉ chọn theo tín hiệu).
 *  - `claims` — mỗi MỆNH ĐỀ sự thật của câu ⇒ cái chứng minh nó (tín hiệu · kết quả của cửa · chính tình huống). Máy đo và lưới domain đọc.
 *  - `silent` — kết quả này phủ định tiền đề của thẻ (vd lời báo vừa được đối chiếu hết) ⇒ thẻ IM ở lượt này.
 *
 * Chọn: biến thể ĐẦU TIÊN (theo thứ tự khai) khớp cả `outcomes` lẫn `when`; không có ⇒ `BASE` (chỉ khi `ready`/thẻ không nút) ⇒ biến thể
 * `unknown` (luôn có, vô điều kiện — lưới `card-variants.test.ts`). Biến thể "không nút" KHÔNG BAO GIỜ chứa lời mời làm việc của nút (Q3).
 */

/**
 * 🔴 US-AIV2-tourguide-0006 (@UC0366 · TG-INV-7 · QĐ PO Q7) — kết quả của BỘ LỌC CHÍNH SÁCH dẫn đường (tách khỏi `ready()`: cửa VẪN làm được,
 * Người dẫn đường chỉ KHÔNG MỜI). Có câu riêng ⇒ là một kết quả của nút như lý do `ready()`; chính sách khác (vừa huỷ · vừa xong) ⇒ câu `unknown`.
 */
export const GUIDE_POLICY_OUTCOMES = ["RECENTLY_REMINDED"] as const;
export type GuidePolicyOutcome = (typeof GUIDE_POLICY_OUTCOMES)[number];

/** Kết quả `ready()` của nút thẻ như câu thẻ nhìn thấy. `unknown` gồm cả "không đọc được" lẫn "nút không dựng được". */
export const GUIDE_CARD_OUTCOMES = ["ready", "unknown", ...AI_NOT_READY_REASONS, ...GUIDE_POLICY_OUTCOMES] as const;
export type GuideCardOutcome = (typeof GUIDE_CARD_OUTCOMES)[number];
/** Kết quả KHÔNG nút. */
export type GuideNoButtonOutcome = Exclude<GuideCardOutcome, "ready">;

/**
 * Cái chứng minh một mệnh đề:
 *  - tên tín hiệu — mệnh đề đúng vì tín hiệu đó (phải nằm trong `when` của thẻ / biến thể hoặc là chỗ trống số của nó);
 *  - `"door"` — mệnh đề là lời của CỬA: `ready()` vừa nói làm được (biến thể `ready`) hoặc nói đúng lý do (biến thể của lý do đó);
 *  - `"trigger"` — mệnh đề là chính tình huống (bị chặn · việc vừa hỏng) — tầng api chỉ phát trigger khi nó xảy ra.
 */
export type GuideClaimProof = GuideSignalKey | "door" | "trigger";

export interface GuideClaim {
  /** Đoạn câu (nguyên văn trong template, kể cả chỗ trống) mà mệnh đề nằm ở đó. */
  readonly clause: string;
  readonly proof: GuideClaimProof;
}

export interface GuideCardVariant {
  /** Id ỔN ĐỊNH `<id thẻ>/<đuôi>` — bảng PO duyệt và máy đo trỏ vào nó. */
  readonly id: string;
  readonly when?: readonly GuidePredicate[];
  readonly outcomes?: readonly GuideCardOutcome[];
  /** Câu; vắng khi `silent`. */
  readonly templateVi?: string;
  /** Chỗ trống số RIÊNG của biến thể (tín hiệu không biết ⇒ biến thể không khớp, thẻ vẫn nói bằng biến thể khác). */
  readonly vars?: Readonly<Record<string, GuideNumericSignalKey>>;
  readonly claims?: readonly GuideClaim[];
  readonly silent?: true;
  /**
   * 🔴 US-AIV2-tourguide-0006 (@UC0367) — phần câu còn lại khi câu trả lời của trang ĐÃ nói vế chính của thẻ (`journey.outcomes.<kết quả>.covers`).
   * Chỉ cho biến thể `ready` mang một sự thật mà câu trang không nói (vd Q1 "nhóm chưa có tài khoản"); vắng ⇒ thẻ bị phủ bỏ cả câu như trước.
   * Phải là một đoạn của chính `templateVi` (lưới `card-variants.test.ts`) — không phải câu mới.
   */
  readonly whenCovered?: string;
  /**
   * 🔴 GUIDE-FIX-20261020 (máy đo audit-after2 · Q1 · V-3) — câu thay khi thẻ `card` CŨNG NÓI ở CÙNG một lượt nhiều dòng (khử trùng ở tầng GHÉP —
   * `guideTextsWithCards`, `render-guide.ts`). Chỉ áp khi dòng của thẻ kia THẬT SỰ có mặt (sau trần nút, chọn câu, bộ lọc chính sách) — không theo vai/tín
   * hiệu: lượt một dòng (sau việc xong) · thẻ kia im/không vai ⇒ câu gốc nguyên văn (vế mà thẻ kia nói vẫn còn). Vd Q1: «Nhóm chưa có tài khoản…» bỏ khỏi
   * câu nợ khi `DUTY-NO-BANK-ACCOUNT` đứng cùng lượt.
   */
  readonly withCard?: { readonly card: string; readonly templateVi: string };
  /** Một câu cho bảng PO: vì sao biến thể này tồn tại (không lên màn hình). */
  readonly note?: string;
}

/**
 * 🔴 Lý do "chưa làm được" mà `ready()` của TỪNG trang đích có thể trả — khai ở đây (domain không đọc được mã api), và lưới api
 * `guide-card-variants.invariants.spec.ts` TỰ KHÁM PHÁ lại từ mã binding (mọi chuỗi `"<LÝ DO>"` trong file binding của trang) ⇒ binding
 * thêm một lý do mà bảng này quên ⇒ đỏ, nêu tên trang + lý do. Trang vắng = `ready()` không bao giờ trả `not_ready` (redirect · luôn ready).
 */
export const GUIDE_TARGET_NOT_READY_REASONS: Readonly<Record<string, readonly AiNotReadyReason[]>> = Object.freeze({
  // G-4 GUIDE-FROZEN-FUND: `FUND_FROZEN` — việc của trang chỉ còn ở quỹ đóng băng.
  "PAY-REPORT": ["NOTHING_TO_REPORT", "NOT_SELF_REPORTABLE", "ALREADY_REPORTED", "NOTHING_OWED", "SUBJECT_GONE", "FUND_FROZEN"],
  "PAY-CONFIRM-DECIDE": ["NOTHING_TO_CONFIRM", "CLAIM_NEEDS_SCREEN", "SUBJECT_GONE", "FUND_FROZEN"],
  // AI-SO-DRIFT-1012 (@UC0217): thẻ yêu cầu chi chờ duyệt — danh sách khoản chờ người này quyết vừa rỗng.
  "EXP-APPROVE": ["NOTHING_TO_DECIDE"],
  "DEBT-REMIND": ["NOTHING_TO_REMIND", "NO_REACHABLE_MEMBER", "SUBJECT_GONE", "FUND_FROZEN"],
  "COL-EQUAL": ["NO_FUND_TO_COLLECT"],
  "ONB-ADD-MEMBERS": ["GROUP_KIND_UNSUPPORTED"],
  // US-AIV2-tourguide-0011 (review #1215 MEDIUM-2): nút thẻ mang khoản của lời đề nghị — đề nghị đã được trả lời/huỷ ⇒ không còn.
  // review #1437 LOW-3: lời đề nghị còn nhưng quỹ khoá / nhóm đình chỉ (khuôn G-4).
  // AI-MENU-POB-ENTRY (@UC0218): nút KHÔNG đối tượng (≥2 lời đề nghị) — lời đề nghị vừa hết giữa phép đếm và `ready()`, và không đóng hộ được ai.
  "PAY-ON-BEHALF": ["SUBJECT_GONE", "FUND_FROZEN", "NOTHING_TO_PAY_ON_BEHALF"],
});

/** Kết quả CHÍNH SÁCH có thể áp cho nút tới trang (`policy.ts`). Trang vắng = không chính sách nào có câu riêng. */
export const GUIDE_TARGET_POLICY_OUTCOMES: Readonly<Record<string, readonly GuidePolicyOutcome[]>> = Object.freeze({
  "DEBT-REMIND": ["RECENTLY_REMINDED"],
});

/** Mọi kết quả mà nút tới `page` có thể có lúc HIỆN: `ready` · `unknown` · lý do của trang · kết quả chính sách. */
export function guideOutcomesFor(page: string): readonly GuideCardOutcome[] {
  return ["ready", "unknown", ...(GUIDE_TARGET_NOT_READY_REASONS[page] ?? []), ...(GUIDE_TARGET_POLICY_OUTCOMES[page] ?? [])];
}

/**
 * Mọi kết quả nút của một THẺ: nút tĩnh ⇒ kết quả của trang đích (`guideOutcomesFor`); nút ĐỘNG (`ctaFrom` — US-AIV2-tourguide-0007a: trang đổi
 * theo người) ⇒ MỌI kết quả (`GUIDE_CARD_OUTCOMES`); không nút ⇒ `[]`. Một hàm cho `journey()` và mọi lưới (không bản chép thứ hai).
 */
export function guideCardOutcomes(card: { readonly cta?: { readonly page: string }; readonly ctaFrom?: string }): readonly GuideCardOutcome[] {
  if (card.ctaFrom !== undefined) return GUIDE_CARD_OUTCOMES;
  return card.cta === undefined ? [] : guideOutcomesFor(card.cta.page);
}

/**
 * 🔴 Lời MỜI làm việc của nút — biến thể không nút không được chứa (Q3: "giữ câu, bỏ nút" mà câu vẫn mời là nút ma). Bộ chung cho mọi thẻ;
 * từng thẻ còn có mệnh đề `proof: "door"` của biến thể `ready` — lưới cấm cả hai trong biến thể không nút.
 */
export const GUIDE_INVITE_RE = /bấm|bên dưới|nút|báo lại|(?<!lời )báo đã chuyển|có thể nhắc|thử lại/iu;
